(() => {
  "use strict";

  const SHOPIFY_KEYS = Object.freeze({
    caffeine: "filter.p.m.custom.has_caffeine",
    zero_sugar: "filter.p.m.custom.zero_sugar",
    zero_calories: "filter.p.m.custom.zero_calories",
    hop_levels: "filter.p.m.custom.hop_levels",
    hop_types: "filter.p.m.custom.hop_types",
    tea_types: "filter.p.m.custom.tea_types",
  });

  const PRETTY_KEYS = Object.freeze(["has_caffeine", "caffeine_free", "zero_sugar", "zero_calories", "hop_levels", "hop_types", "tea_types"]);

  const selectors = Object.freeze({
    filterContainer: "[data-collection-filters]",
    container: "[data-collection-container]",
    grid: "[data-collection-grid]",
    status: "#collection-status",
    filterLink: "[data-collection-filters] a[data-filter-key][data-filter-value]",
    clear: "[data-filter-clear]",
    sortSelect: '[data-collection-sort] select[name="sort_by"]',
    sortWrapper: "[data-collection-sort]",
    customSelect: "[data-custom-select]",
    customTrigger: ".custom-select__trigger",
    customMenu: ".custom-select__menu",
    customOption: '[role="option"][data-value]',
    nextPage: "[data-next-page]",
    filterToggle: "[data-filter-toggle]",
    filterClose: "[data-filter-close]",
  });

  const filterContainer = document.querySelector(selectors.filterContainer);
  const grid = document.querySelector(selectors.grid);
  const container = document.querySelector(selectors.container);
  if (!filterContainer || !grid || !container) return;

  const statusEl = document.querySelector(selectors.status);
  let activeRequestController = null;

  function announce(message) {
    if (!statusEl) return;
    statusEl.textContent = "";
    window.setTimeout(() => {
      statusEl.textContent = message;
    }, 20);
  }

  // -----------------------------
  // Enter animation (infinite scroll appended items)
  // -----------------------------
  function animateEnter(nodes) {
    const els = Array.from(nodes || []).filter(Boolean);
    if (!els.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    // Ensure start state is set before any paint
    els.forEach((el) => {
      el.classList.add("is-enter");
      el.classList.remove("is-enter-active");
      el.style.transitionDelay = "";
    });

    // Force the browser to commit initial styles before activating
    // Reading layout triggers a style flush.
    void document.body.offsetHeight;

    window.requestAnimationFrame(() => {
      const stepDelay = 150; // ms
      const maxDelay = (els.length - 1) * stepDelay;

      // must match CSS durations
      const maxDuration = 500;
      const buffer = 200;

      els.forEach((el, i) => {
        el.style.transitionDelay = `${i * stepDelay}ms`;
        el.classList.add("is-enter-active");
      });

      window.setTimeout(
        () => {
          els.forEach((el) => {
            el.classList.remove("is-enter", "is-enter-active");
            el.style.transitionDelay = "";
          });
        },
        maxDelay + maxDuration + buffer,
      );
    });
  }

  function isModifiedClick(e) {
    return e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
  }

  function getUrl() {
    return new URL(window.location.href);
  }

  function pushUrl(u) {
    window.history.pushState({}, "", u.toString());
  }

  function replaceUrl(u) {
    window.history.replaceState({}, "", u.toString());
  }

  function getPrettyValue(el) {
    return el.getAttribute("data-pretty-value") || el.getAttribute("data-filter-value");
  }

  function buildHopsMap(containerEl, key) {
    const map = new Map();
    containerEl.querySelectorAll(`a[data-filter-key="${key}"][data-filter-value][data-pretty-value]`).forEach((a) => {
      const pretty = (a.getAttribute("data-pretty-value") || "").toLowerCase().trim();
      const gid = a.getAttribute("data-filter-value");
      if (pretty && gid) map.set(pretty, gid);
    });
    return map;
  }

  function syncEmptyPrettyActiveStates() {
    const u = new URL(window.location.href);

    function sync(prettyKey, shopifyKey) {
      const selected = u.searchParams.getAll(prettyKey).map((v) => v.toLowerCase().trim());

      filterContainer.querySelectorAll(`a[data-filter-key="${shopifyKey}"].is-empty[data-pretty-value]`).forEach((a) => {
        const val = (a.getAttribute("data-pretty-value") || "").toLowerCase().trim();
        const on = selected.includes(val);
        a.classList.toggle("is-active", on);
        a.setAttribute("aria-current", on ? "true" : "false");
      });
    }

    sync("hop_types", SHOPIFY_KEYS.hop_types);
    sync("hop_levels", SHOPIFY_KEYS.hop_levels);
    sync("tea_types", SHOPIFY_KEYS.tea_types);
  }

  // -----------------------------
  // Filter panel open/close (mobile overlay)
  // -----------------------------
  function lockBodyScroll(locked) {
    document.documentElement.classList.toggle("filters-open", locked);
    document.body.classList.toggle("filters-open", locked);
  }

  function setPanelOpen(open) {
    container.classList.toggle("is-open", open);

    const btn = document.querySelector(selectors.filterToggle);
    if (btn) btn.setAttribute("aria-expanded", open ? "true" : "false");

    lockBodyScroll(open);
  }

  function closePanel({ restoreFocus = true } = {}) {
    if (!container.classList.contains("is-open")) return;
    setPanelOpen(false);

    if (restoreFocus) {
      const btn = document.querySelector(selectors.filterToggle);
      if (btn) btn.focus();
    }
  }

  function openPanel() {
    if (container.classList.contains("is-open")) return;
    setPanelOpen(true);

    // focus close button if present, otherwise focus the panel
    const closeBtn = filterContainer.querySelector(selectors.filterClose);
    if (closeBtn) closeBtn.focus();
    else filterContainer.focus?.();
  }

  function setupFilterPanelToggle() {
    const btn = document.querySelector(selectors.filterToggle);
    if (!btn) return;

    // wire ARIA controls to the panel container (created in snippet)
    const panelEl = filterContainer.querySelector(".collection-filters__container");
    if (panelEl && !panelEl.id) panelEl.id = `collection-filters-${Math.random().toString(16).slice(2)}`;
    if (panelEl) btn.setAttribute("aria-controls", panelEl.id);

    if (!btn.hasAttribute("aria-expanded")) btn.setAttribute("aria-expanded", "false");

    if (btn.dataset.bound === "true") return;
    btn.dataset.bound = "true";

    btn.addEventListener("click", () => {
      const open = !container.classList.contains("is-open");
      open ? openPanel() : closePanel();
    });
  }

  function setupFilterCloseButton() {
    const closeBtn = filterContainer.querySelector(selectors.filterClose);
    if (!closeBtn) return;

    if (closeBtn.dataset.bound === "true") return;
    closeBtn.dataset.bound = "true";

    closeBtn.addEventListener("click", () => closePanel());
  }

  function setupEscapeToClose() {
    if (document.documentElement.dataset.filtersEscBound === "true") return;
    document.documentElement.dataset.filtersEscBound = "true";

    document.addEventListener("keydown", (e) => {
      if (e.key !== "Escape") return;
      if (!container.classList.contains("is-open")) return;
      if (!isMobileFilterMode()) return;
      closePanel();
    });
  }
  // Close on outside click (mobile)
  function isMobileFilterMode() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  function setupOutsideClickToClose() {
    if (document.documentElement.dataset.filtersOutsideBound === "true") return;
    document.documentElement.dataset.filtersOutsideBound = "true";

    document.addEventListener(
      "pointerdown",
      (e) => {
        if (!container.classList.contains("is-open")) return;
        if (!isMobileFilterMode()) return;

        const target = e.target;

        if (filterContainer.contains(target)) return;

        const toggleBtn = document.querySelector(selectors.filterToggle);
        if (toggleBtn && toggleBtn.contains(target)) return;

        const closeBtn = filterContainer.querySelector(selectors.filterClose);
        if (closeBtn && closeBtn.contains(target)) return;

        closePanel({ restoreFocus: false });
      },
      { capture: true },
    );
  }

  // -----------------------------
  // Infinite scroll
  // -----------------------------
  let infiniteObserver = null;
  let infiniteSentinel = null;

  function setupInfiniteScroll() {
    if (infiniteObserver) infiniteObserver.disconnect();
    if (infiniteSentinel) infiniteSentinel.remove();

    const nextLink = document.querySelector(selectors.nextPage);
    if (!nextLink) return;

    let loading = false;

    infiniteSentinel = document.createElement("div");
    infiniteSentinel.setAttribute("data-scroll-sentinel", "");
    infiniteSentinel.style.height = "1px";
    container.after(infiniteSentinel);

    infiniteObserver = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || loading) return;

        const liveNext = document.querySelector(selectors.nextPage);
        if (!liveNext) {
          infiniteObserver.disconnect();
          return;
        }

        loading = true;

        try {
          const res = await fetch(liveNext.href, {
            headers: { "X-Requested-With": "XMLHttpRequest" },
          });
          if (!res.ok) throw new Error("Failed");

          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, "text/html");

          const newItems = Array.from(doc.querySelectorAll(".collection-product"));
          if (newItems.length) {
            // set initial hidden state BEFORE inserting into DOM
            newItems.forEach((el) => {
              el.classList.add("is-enter");
              el.classList.remove("is-enter-active");
              el.style.transitionDelay = "";
            });

            const frag = document.createDocumentFragment();
            newItems.forEach((el) => frag.appendChild(el));
            grid.appendChild(frag);

            animateEnter(newItems);
          }

          const newNext = doc.querySelector(selectors.nextPage);
          liveNext.remove();
          if (newNext) {
            grid.after(newNext);
          } else {
            infiniteObserver.disconnect();
          }

          announce("More products loaded.");
        } catch (e) {
          console.error(e);
        } finally {
          loading = false;
        }
      },
      { rootMargin: "500px" },
    );

    infiniteObserver.observe(infiniteSentinel);
  }

  // -----------------------------
  // Pretty URL manipulation
  // -----------------------------
  function sanitizePretty(u) {
    const has = u.searchParams.get("has_caffeine") === "1";
    const free = u.searchParams.get("caffeine_free") === "1";
    if (has && free) u.searchParams.delete("caffeine_free");
    return u;
  }

  function clearPrettyAll(u) {
    PRETTY_KEYS.forEach((k) => u.searchParams.delete(k));
    return u;
  }

  function setCaffeinePretty(u, nextKey, on) {
    u.searchParams.delete("has_caffeine");
    u.searchParams.delete("caffeine_free");
    if (on) u.searchParams.set(nextKey, "1");
    return u;
  }

  function setTogglePretty(u, key, on) {
    if (on) u.searchParams.set(key, "1");
    else u.searchParams.delete(key);
    return u;
  }

  function toggleMultiPretty(u, key, value, shouldRemove) {
    const existing = u.searchParams.getAll(key);
    u.searchParams.delete(key);

    if (shouldRemove) {
      existing.filter((v) => v !== value).forEach((v) => u.searchParams.append(key, v));
    } else {
      const next = existing.includes(value) ? existing : existing.concat(value);
      next.forEach((v) => u.searchParams.append(key, v));
    }
    return u;
  }

  function deriveShopifyUrlFromPretty(prettyUrl) {
    const u = new URL(prettyUrl.toString());
    const next = new URL(prettyUrl.toString());

    Object.values(SHOPIFY_KEYS).forEach((k) => next.searchParams.delete(k));

    const sortBy = u.searchParams.get("sort_by");
    if (sortBy) next.searchParams.set("sort_by", sortBy);
    else next.searchParams.delete("sort_by");

    const has = u.searchParams.get("has_caffeine") === "1";
    const free = u.searchParams.get("caffeine_free") === "1";
    if (has) next.searchParams.set(SHOPIFY_KEYS.caffeine, "1");
    else if (free) next.searchParams.set(SHOPIFY_KEYS.caffeine, "0");

    if (u.searchParams.get("zero_sugar") === "1") next.searchParams.set(SHOPIFY_KEYS.zero_sugar, "1");
    if (u.searchParams.get("zero_calories") === "1") next.searchParams.set(SHOPIFY_KEYS.zero_calories, "1");

    const hopLevelsPretty = u.searchParams.getAll("hop_levels");
    if (hopLevelsPretty.length) {
      const map = buildHopsMap(filterContainer, SHOPIFY_KEYS.hop_levels);
      hopLevelsPretty.forEach((label) => {
        const key = (label || "").toLowerCase().trim();
        const gid = map.get(key);
        if (gid) next.searchParams.append(SHOPIFY_KEYS.hop_levels, gid);
      });
    }

    const hopTypesPretty = u.searchParams.getAll("hop_types");
    if (hopTypesPretty.length) {
      const map = buildHopsMap(filterContainer, SHOPIFY_KEYS.hop_types);
      hopTypesPretty.forEach((label) => {
        const key = (label || "").toLowerCase().trim();
        const gid = map.get(key);
        if (gid) next.searchParams.append(SHOPIFY_KEYS.hop_types, gid);
      });
    }

    const teaTypesPretty = u.searchParams.getAll("tea_types");
    if (teaTypesPretty.length) {
      const map = buildHopsMap(filterContainer, SHOPIFY_KEYS.tea_types);
      teaTypesPretty.forEach((label) => {
        const key = (label || "").toLowerCase().trim();
        const gid = map.get(key);
        if (gid) next.searchParams.append(SHOPIFY_KEYS.tea_types, gid);
      });
    }

    return next;
  }

  // -----------------------------
  // Custom Select (Sort By)
  // -----------------------------
  let customSortDocClickBound = false;

  function closeCustomSelect(wrapper) {
    const trigger = wrapper.querySelector(selectors.customTrigger);
    const menu = wrapper.querySelector(selectors.customMenu);
    if (!trigger || !menu) return;

    trigger.setAttribute("aria-expanded", "false");
    menu.classList.remove("is-open");
    menu.setAttribute("aria-hidden", "true");
  }

  function openCustomSelect(wrapper) {
    const trigger = wrapper.querySelector(selectors.customTrigger);
    const menu = wrapper.querySelector(selectors.customMenu);
    if (!trigger || !menu) return;

    trigger.setAttribute("aria-expanded", "true");
    menu.classList.add("is-open");
    menu.setAttribute("aria-hidden", "false");
    menu.focus();
  }

  function toggleCustomSelect(wrapper) {
    const trigger = wrapper.querySelector(selectors.customTrigger);
    if (!trigger) return;
    const expanded = trigger.getAttribute("aria-expanded") === "true";
    expanded ? closeCustomSelect(wrapper) : openCustomSelect(wrapper);
  }

  function syncCustomSelectUI(wrapper) {
    const menu = wrapper.querySelector(selectors.customMenu);
    if (!menu) return;

    const u = getUrl();
    const value = u.searchParams.get("sort_by") || "";

    const options = Array.from(menu.querySelectorAll(selectors.customOption));
    options.forEach((opt) => {
      const optVal = opt.getAttribute("data-value") || "";
      const isSelected = (value !== "" && optVal === value) || (value === "" && optVal === "{{ collection.default_sort_by }}");
      opt.setAttribute("aria-selected", isSelected ? "true" : "false");
      opt.classList.toggle("is-selected", isSelected);
    });

    const active = menu.querySelector(`${selectors.customOption}[aria-selected="true"]`);
    if (active && !active.id) active.id = `sort-opt-${Math.random().toString(16).slice(2)}`;
    if (active) menu.setAttribute("aria-activedescendant", active.id);
  }

  function applySortValue(selectEl, value) {
    selectEl.value = value;

    const u = getUrl();
    const firstValue = selectEl.querySelector("option")?.value;

    if (!value || (firstValue && value === firstValue)) u.searchParams.delete("sort_by");
    else u.searchParams.set("sort_by", value);

    pushUrl(u);
    applyPrettyAndFetch({ announceText: "Sort updated." });
  }

  function initCustomSort() {
    const sortForm = document.querySelector(selectors.sortWrapper);
    if (!sortForm) return;

    const wrapper = sortForm.querySelector(selectors.customSelect);
    if (!wrapper) return;

    const trigger = wrapper.querySelector(selectors.customTrigger);
    const menu = wrapper.querySelector(selectors.customMenu);
    const nativeSelect = wrapper.querySelector(selectors.sortSelect);

    if (!trigger || !menu || !nativeSelect) return;

    if (wrapper.dataset.bound === "true") {
      syncCustomSelectUI(wrapper);
      return;
    }
    wrapper.dataset.bound = "true";

    if (!menu.id) menu.id = `sort-menu-${Math.random().toString(16).slice(2)}`;
    trigger.setAttribute("aria-controls", menu.id);
    menu.setAttribute("role", "listbox");
    menu.setAttribute("aria-hidden", "true");

    const optionEls = Array.from(menu.querySelectorAll(selectors.customOption));
    optionEls.forEach((opt) => {
      opt.setAttribute("role", "option");
      opt.setAttribute("tabindex", "-1");
    });

    syncCustomSelectUI(wrapper);

    trigger.addEventListener("click", () => toggleCustomSelect(wrapper));

    trigger.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openCustomSelect(wrapper);
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        openCustomSelect(wrapper);
        const first = menu.querySelector(selectors.customOption);
        if (first) first.scrollIntoView({ block: "nearest" });
      }
    });

    menu.addEventListener("keydown", (e) => {
      const opts = Array.from(menu.querySelectorAll(selectors.customOption));
      const current = menu.querySelector(`${selectors.customOption}[aria-selected="true"]`) || opts[0];
      let idx = Math.max(0, opts.indexOf(current));

      if (e.key === "Escape") {
        e.preventDefault();
        closeCustomSelect(wrapper);
        trigger.focus();
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        idx = Math.min(opts.length - 1, idx + 1);
        const next = opts[idx];
        if (next) {
          opts.forEach((o) => o.setAttribute("aria-selected", "false"));
          next.setAttribute("aria-selected", "true");
          menu.setAttribute("aria-activedescendant", next.id || "");
          next.scrollIntoView({ block: "nearest" });
        }
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        idx = Math.max(0, idx - 1);
        const prev = opts[idx];
        if (prev) {
          opts.forEach((o) => o.setAttribute("aria-selected", "false"));
          prev.setAttribute("aria-selected", "true");
          menu.setAttribute("aria-activedescendant", prev.id || "");
          prev.scrollIntoView({ block: "nearest" });
        }
        return;
      }

      if (e.key === "Enter") {
        e.preventDefault();
        const selected = menu.querySelector(`${selectors.customOption}[aria-selected="true"]`);
        if (selected) {
          const value = selected.getAttribute("data-value");
          closeCustomSelect(wrapper);
          syncCustomSelectUI(wrapper);
          applySortValue(nativeSelect, value);
          trigger.focus();
        }
        return;
      }
    });

    menu.addEventListener("click", (e) => {
      const opt = e.target.closest(selectors.customOption);
      if (!opt) return;

      const value = opt.getAttribute("data-value");
      closeCustomSelect(wrapper);
      applySortValue(nativeSelect, value);
      syncCustomSelectUI(wrapper);
      trigger.focus();
    });

    if (!customSortDocClickBound) {
      customSortDocClickBound = true;
      document.addEventListener("click", (e) => {
        document.querySelectorAll(selectors.customSelect).forEach((w) => {
          if (!w.contains(e.target)) closeCustomSelect(w);
        });
      });
    }
  }

  // -----------------------------
  // AJAX section swap
  // -----------------------------
  async function swapCollection(fetchUrl, { announceText } = {}) {
    if (activeRequestController) activeRequestController.abort();
    activeRequestController = new AbortController();

    grid.setAttribute("aria-busy", "true");

    try {
      const res = await fetch(fetchUrl.toString(), {
        signal: activeRequestController.signal,
        headers: { "X-Requested-With": "XMLHttpRequest" },
      });
      if (!res.ok) throw new Error("Request failed");

      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      const newGrid = doc.querySelector(selectors.grid);
      const newFilters = doc.querySelector(selectors.filterContainer);

      if (!newGrid) throw new Error("Grid not found in response");

      grid.innerHTML = newGrid.innerHTML;

      const newNativeSort = doc.querySelector(selectors.sortSelect);
      const currentNativeSort = document.querySelector(selectors.sortSelect);
      if (newNativeSort && currentNativeSort) currentNativeSort.value = newNativeSort.value;

      if (newFilters) filterContainer.innerHTML = newFilters.innerHTML;

      setupFilterPanelToggle();
      setupFilterCloseButton();
      setupEscapeToClose();
      setupOutsideClickToClose();

      syncEmptyPrettyActiveStates();
      setupInfiniteScroll();
      initCustomSort();

      announce(announceText || "Products updated.");
    } catch (err) {
      if (err && err.name === "AbortError") return;
      window.location.href = fetchUrl.toString();
    } finally {
      grid.setAttribute("aria-busy", "false");
    }
  }

  function applyPrettyAndFetch({ announceText } = {}) {
    const pretty = sanitizePretty(getUrl());
    replaceUrl(pretty);
    const fetchUrl = deriveShopifyUrlFromPretty(pretty);
    return swapCollection(fetchUrl, { announceText });
  }

  // -----------------------------
  // Filter interactions
  // -----------------------------
  function onFilterClick(e) {
    if (isModifiedClick(e)) return;

    const clearEl = e.target.closest(selectors.clear);
    const filterLink = e.target.closest(selectors.filterLink);

    if (!clearEl && !filterLink) return;
    if (!filterContainer.contains(clearEl || filterLink)) return;
    if (filterLink && filterLink.getAttribute("aria-disabled") === "true") return;

    e.preventDefault();

    const u = getUrl();

    if (clearEl) {
      pushUrl(clearPrettyAll(u));
      applyPrettyAndFetch({ announceText: "Filters cleared. Products updated." });
      return;
    }

    const key = filterLink.getAttribute("data-filter-key");
    const value = filterLink.getAttribute("data-filter-value");
    const isActive = filterLink.getAttribute("aria-current") === "true" || filterLink.classList.contains("is-active");

    if (key === SHOPIFY_KEYS.caffeine) {
      const prettyKey = value === "1" ? "has_caffeine" : "caffeine_free";
      pushUrl(setCaffeinePretty(u, prettyKey, !isActive));
    } else if (key === SHOPIFY_KEYS.zero_sugar) {
      pushUrl(setTogglePretty(u, "zero_sugar", !isActive));
    } else if (key === SHOPIFY_KEYS.zero_calories) {
      pushUrl(setTogglePretty(u, "zero_calories", !isActive));
    } else if (key === SHOPIFY_KEYS.hop_levels) {
      pushUrl(toggleMultiPretty(u, "hop_levels", getPrettyValue(filterLink), isActive));
    } else if (key === SHOPIFY_KEYS.hop_types) {
      pushUrl(toggleMultiPretty(u, "hop_types", getPrettyValue(filterLink), isActive));
    } else if (key === SHOPIFY_KEYS.tea_types) {
      pushUrl(toggleMultiPretty(u, "tea_types", getPrettyValue(filterLink), isActive));
    } else {
      window.location.href = filterLink.href;
      return;
    }

    applyPrettyAndFetch({ announceText: "Products updated." });
  }

  document.addEventListener("click", onFilterClick);

  window.addEventListener("popstate", () => {
    applyPrettyAndFetch({ announceText: "Products updated." });
  });

  // -----------------------------
  // Initial
  // -----------------------------
  (() => {
    const u = getUrl();
    const hasAny =
      u.searchParams.get("has_caffeine") === "1" ||
      u.searchParams.get("caffeine_free") === "1" ||
      u.searchParams.get("zero_sugar") === "1" ||
      u.searchParams.get("zero_calories") === "1" ||
      u.searchParams.getAll("hop_levels").length > 0 ||
      u.searchParams.getAll("hop_types").length > 0 ||
      u.searchParams.getAll("tea_types").length > 0;

    if (hasAny) applyPrettyAndFetch({ announceText: "Products updated." });

    syncEmptyPrettyActiveStates();
    setupInfiniteScroll();

    setupFilterPanelToggle();
    setupFilterCloseButton();
    setupEscapeToClose();
    setupOutsideClickToClose();

    initCustomSort();
  })();
})();
